import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import InputText from "@/components/InputText/InputText";
import Button from "@/components/Button/Button";
import { FormMessage } from "@/components/FormMessage/FormMessage";
import logo from "@/assets/logo.svg";
import * as S from "./SetPassword.styles";

type Status = "checking" | "invalid" | "ready" | "done";

export function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [status, setStatus] = useState<Status>("checking");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<[string, boolean] | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }

    fetch(`/v1/set-password/${token}`)
      .then(async (response) => {
        if (!response.ok) throw new Error();
        const data = await response.json();
        setUsername(data.username);
        setStatus("ready");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const formData = new FormData(e.currentTarget);
      const password = formData.get("password") as string;
      const confirmPassword = formData.get("confirmPassword") as string;

      if (password !== confirmPassword) {
        throw new Error("Passwords don't match");
      }

      const response = await fetch(`/v1/set-password/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Couldn't set password");
      }

      setStatus("done");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Couldn't set password";
      setMessage([message, false]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <S.Page>
      <S.Card>
        <S.Brand><img src={logo} alt="" /><span>Embark</span></S.Brand>

        {status === "checking" && (
          <S.Confirmation>
            <p>Checking your link...</p>
          </S.Confirmation>
        )}

        {status === "invalid" && (
          <S.Confirmation>
            <h1>Link no longer valid</h1>
            <p>This set-password link has expired or already been used. Contact an admin if you need a new one.</p>
          </S.Confirmation>
        )}

        {status === "done" && (
          <S.Confirmation>
            <h1>You're all set</h1>
            <p>Your password has been updated. You can now log in to Emby with your new credentials.</p>
          </S.Confirmation>
        )}

        {status === "ready" && (
          <S.Form onSubmit={handleSubmit}>
            <h1>Set your password</h1>
            <p className="subtitle">Choose a password for {username}.</p>
            <InputText
              label="New password"
              id="password"
              name="password"
              autoComplete="new-password"
              type="password"
              placeholder="New password"
              minLength={8}
              required
            />
            <InputText
              label="Confirm password"
              id="confirmPassword"
              name="confirmPassword"
              autoComplete="new-password"
              type="password"
              placeholder="Confirm password"
              minLength={8}
              required
            />
            {message && <FormMessage message={message} />}
            <Button disabled={loading}>{loading ? "..." : "Set password"}</Button>
          </S.Form>
        )}
      </S.Card>
    </S.Page>
  );
}

export default SetPassword;
